import React, { useState } from 'react';
import Button from '../../../common/Button';
import { File } from 'react-feather';
import { Modal, Tooltip } from 'antd';
import Skeleton from '../../../common/Skeleton';
import {
  defaultResponseHandler,
  useAssetTranslation,
  getAssetSubType,
} from '@mss/mss-component-library';
import { TableComponent } from '../../util';
import { useQuery } from '@tanstack/react-query';
import Fetch from '../../../common/util/_fetch';

type Props = {
  label: string;
  number: number;
  tooltip: string;
  loading: boolean;
  url: string;
  tableComponent: TableComponent;
  groupName: string;
  color: string | undefined;
  areAssets?: boolean;
};

type ModalProps = {
  visible: boolean;
  url: string;
  setIsOpenModal: (isOpen: boolean) => void;
  Component: TableComponent;
  title: string;
  areAssets?: boolean;
};

const DataModal = ({
  title,
  visible,
  url,
  setIsOpenModal,
  Component,
  areAssets,
}: ModalProps) => {
  const { t } = useAssetTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ['status', url],
    queryFn: async () =>
      (
        await defaultResponseHandler<Record<string, any>[]>({
          request: Fetch.get(url),
          t,
        })
      ).data.map(x => {
        const flatData = {
          ...x,
          ...(x.data ?? {}),
        };

        if (!areAssets) {
          return flatData;
        }
        return {
          ...flatData,
          subType: flatData[getAssetSubType(flatData.type)],
          custodian_id: flatData.custodian?.id,
          custodian_name: flatData.custodian?.name,
        };
      }) ?? [],
    enabled: visible,
  });

  const closeModal = () => setIsOpenModal(false);

  return (
    <Modal
      title={title}
      className="w-[1300px] max-w-[calc(100vw-2rem)]"
      onOk={closeModal}
      onCancel={closeModal}
      cancelButtonProps={{ hidden: true }}
      open={visible}
      destroyOnClose={false}
    >
      <Component resources={data ?? []} loading={!data && isLoading} />
    </Modal>
  );
};

const NumericCard = ({
  color,
  label,
  number,
  tooltip,
  loading,
  url,
  tableComponent,
  groupName,
  areAssets,
}: Props) => {
  const [isOpenModal, setIsOpenModal] = useState(false);

  return (
    <>
      <Tooltip title={loading ? '' : tooltip}>
        <div className="h-32 w-32 border-gray-200 border p-2 bg-full-white flex justify-center items-center flex-col mb-2">
          <h1 className="p-0 m-0 text-base leading-3 flex justify-center items-center">
            {color && (
              <span
                className="inline-block h-2 w-2 p-0 mr-2"
                style={{ backgroundColor: color }}
              />
            )}
            {label.toUpperCase()}
          </h1>
          <h2 className={`p-0 ${loading ? 'm-2' : 'm-4'} text-3xl leading-4`}>
            {loading ? <Skeleton className="h-8 w-8" /> : number}
          </h2>
          <Button
            onClick={() => setIsOpenModal(true)}
            disabled={loading}
            className={loading ? 'animate-pulse' : ''}
          >
            <File className="mr-1" size={12} />
            View
          </Button>
        </div>
      </Tooltip>
      <DataModal
        title={`${groupName}: ${label}`}
        visible={isOpenModal}
        url={url}
        setIsOpenModal={setIsOpenModal}
        Component={tableComponent}
        areAssets={areAssets}
      />
    </>
  );
};

export default NumericCard;
